import React, { useRef } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import SaveButton from "../UI/SaveButton";
import useExportChartImage from "../useExportChartImage";
import { getCategoryLabelById } from "../../../utils/labelsById";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const colorPalette = [
  "#f44336",
  "#f48fb1",
  "#f06292",
  "#ef5350",
  "#e57373",
  "#e0e0e0",
  "#81d4fa",
  "#4db6ac",
  "#aed581",
  "#ffd54f",
  "#ffb74d",
  "#a1887f",
];

const HorizontalBarMultiWithTable = ({ data, dataSetting, title }) => {
  const chartRef = useRef();
  const exportChartImage = useExportChartImage();
  const fileName = dataSetting?.name + "_" + dataSetting?.year;

  const groupKeys = Object.keys(data).filter(
    (key) => key.toLowerCase() !== "pozicioni i punës"
  );

  const pozicioneSet = new Set();
  groupKeys.forEach((key) => {
    Object.keys(data[key] || {}).forEach((poz) => pozicioneSet.add(poz));
  });

  const pozicioneKeys = Array.from(pozicioneSet).sort((a, b) => {
    const extractNumber = (s) => {
      const match = s.match(/\d+/g);
      return match ? parseInt(match[0]) : Number.MAX_SAFE_INTEGER;
    };
    return extractNumber(a) - extractNumber(b);
  });

  const allPercValues = groupKeys.flatMap((group) => {
    const total = Object.values(data[group]).reduce((a, b) => a + b, 0);
    return pozicioneKeys.map((poz) => {
      const value = data[group][poz] || 0;
      return total > 0 ? parseFloat(((value / total) * 100).toFixed(2)) : 0;
    });
  });

  const maxPercValue = Math.max(...allPercValues);
  const suggestedMax = Math.min(100, Math.ceil((maxPercValue + 5) / 5) * 5);

  const chartData = {
    labels: pozicioneKeys,
    datasets: groupKeys.map((group, idx) => {
      const cleanGroup = group.replace(/paga - /i, "").trim();
      const total = Object.values(data[group]).reduce((a, b) => a + b, 0);
      return {
        label: cleanGroup,
        data: pozicioneKeys.map((poz) => {
          const value = data[group][poz] || 0;
          return total > 0 ? parseFloat(((value / total) * 100).toFixed(2)) : 0;
        }),
        backgroundColor: colorPalette[idx % colorPalette.length],
      };
    }),
  };

  const options = {
    indexAxis: "y",
    responsive: true,
    plugins: {
      legend: { position: "top" },
      tooltip: { enabled: false },
      datalabels: { display: false },
    },
    scales: {
      x: {
        ticks: {
          callback: (val) => val + "%",
          display: true,
        },
        max: suggestedMax,
      },
      y: {
        ticks: { display: true },
      },
    },
  };

  const handleExportImage = () => {
    exportChartImage({
      chartRef,
      fileName,
      dataSetting,
    });
  };

  return (
    <div>
      {title && (
        <h3 style={{ marginBottom: "20px" }}>{getCategoryLabelById(title)}</h3>
      )}
      <div style={{ display: "grid" }} ref={chartRef}>
        <div style={{ padding: "0.5rem", backgroundColor: "#fff" }}>
          <div style={{ height: "400px" }}>
            <Bar options={options} data={chartData} />
          </div>
        </div>

        <div style={{ padding: "0.5rem", backgroundColor: "#fff" }}>
          <div style={{ overflowX: "auto" }}>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: "14px",
              }}
            >
              <thead>
                <tr style={{ backgroundColor: "#f5f5f5" }}>
                  <th
                    style={{
                      border: "1px solid black",
                      padding: "8px",
                      textAlign: "left",
                    }}
                  >
                    &nbsp;
                  </th>
                  {pozicioneKeys.map((poz) => (
                    <th
                      key={poz}
                      style={{
                        border: "1px solid black",
                        padding: "8px",
                        textAlign: "center",
                      }}
                    >
                      {poz}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {groupKeys.map((group, idx) => {
                  const total = Object.values(data[group]).reduce(
                    (a, b) => a + b,
                    0
                  );
                  const cleanGroup = group.replace(/paga - /i, "").trim();
                  return (
                    <tr key={group}>
                      <td style={{ border: "1px solid black", padding: "8px" }}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          <div
                            style={{
                              width: "14px",
                              height: "14px",
                              backgroundColor:
                                colorPalette[idx % colorPalette.length],
                              border: "1px solid #000",
                            }}
                          ></div>
                          <span style={{ color: "black", fontWeight: "bold" }}>
                            {cleanGroup}
                          </span>
                        </div>
                      </td>
                      {pozicioneKeys.map((poz) => {
                        const value = data[group][poz] || 0;
                        const perc =
                          total > 0
                            ? ((value / total) * 100).toFixed(2)
                            : "0.00";
                        return (
                          <td
                            key={poz}
                            style={{
                              border: "1px solid black",
                              padding: "8px",
                              textAlign: "center",
                              color: "black",
                            }}
                          >
                            {perc}%
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <SaveButton onClick={handleExportImage} />
    </div>
  );
};

export default HorizontalBarMultiWithTable;
